import { useState, useEffect, useCallback } from 'react';
import { networkDocApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Network, FileText, Table2, Copy, AlertTriangle, Loader2, Download, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const COLUMNS = [
  { key: 'sensor_name', label: 'Sensornamn', primary: true },
  { key: 'location', label: 'Placering', primary: true },
  { key: 'department', label: 'Avdelning/byggnad', primary: true },
  { key: 'wifi_mac', label: 'Wi-Fi MAC-adress', primary: true },
  { key: 'serial_number', label: 'Serienummer', primary: true },
  { key: 'device_id', label: 'Device-ID', primary: false },
  { key: 'model', label: 'Modell', primary: false },
  { key: 'firmware', label: 'Firmwareversion', primary: false },
  { key: 'status', label: 'Status', primary: false },
];
const DEFAULT_COLS = COLUMNS.filter(c => c.primary).map(c => c.key);

export function NetworkExportDialog({ open, onOpenChange, schools = [] }) {
  const { isSuperAdmin, isKommunAdmin } = useAuth();
  const tenantWide = isSuperAdmin() || isKommunAdmin();

  const [schoolId, setSchoolId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [format, setFormat] = useState('pdf');
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // school_admin / single-school: auto-select their school
  useEffect(() => {
    if (!open) return;
    setFormat('pdf'); setCols(DEFAULT_COLS); setIncludeInactive(false); setShowMissing(false);
    if (!tenantWide && schools.length >= 1) setSchoolId(schools[0].id);
    else setSchoolId('');
  }, [open]); // eslint-disable-line

  const loadPreview = useCallback(async (id) => {
    if (!id) { setPreview(null); return; }
    setLoadingPreview(true);
    try {
      const res = await networkDocApi.preview(id);
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || 'Kunde inte hämta underlag');
    } finally { setLoadingPreview(false); }
  }, []);

  useEffect(() => { if (open && schoolId) loadPreview(schoolId); }, [open, schoolId, loadPreview]);

  const toggleCol = (key) => setCols(c => c.includes(key) ? c.filter(k => k !== key) : [...c, key]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (!schoolId) { toast.error('Välj en skola'); return; }
    if (cols.length === 0) { toast.error('Välj minst en kolumn'); return; }
    setExporting(true);
    try {
      const res = await networkDocApi.exportDoc({
        school_id: schoolId, format, columns: cols, include_inactive: includeInactive,
      });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `Vapelarm_Natverksunderlag.${format}`;
      downloadBlob(res.data, fname);
      toast.success('Nätverksunderlag exporterat');
    } catch (err) {
      let detail = 'Kunde inte exportera';
      if (err.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text()).detail || detail; } catch { /* noop */ }
      } else { detail = err.response?.data?.detail || detail; }
      toast.error(detail);
    } finally { setExporting(false); }
  };

  const handleCopyMac = async () => {
    if (!preview) return;
    const macs = preview.mac_list || [];
    if (macs.length === 0) { toast.error('Inga Wi-Fi MAC-adresser att kopiera'); return; }
    try {
      await navigator.clipboard.writeText(macs.join('\n'));
      const miss = preview.missing_mac_count || 0;
      toast.success(miss > 0
        ? `${macs.length} MAC-adresser kopierade. ${miss} sensorer saknar MAC-adress.`
        : `${macs.length} MAC-adresser kopierade`);
    } catch { toast.error('Kunde inte kopiera'); }
  };

  const schoolName = schools.find(s => s.id === schoolId)?.name;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="network-export-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo flex items-center gap-2">
            <Network className="h-5 w-5 text-navy" /> Exportera nätverksunderlag
          </DialogTitle>
          <DialogDescription className="font-ibm">
            Tekniskt underlag (Wi-Fi MAC-adresser m.m.) för skolans IT-avdelning.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* School */}
          <div className="space-y-1.5">
            <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Skola</Label>
            {tenantWide ? (
              <Select value={schoolId} onValueChange={setSchoolId}>
                <SelectTrigger data-testid="export-school-select"><SelectValue placeholder="Välj skola…" /></SelectTrigger>
                <SelectContent>
                  {schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-ibm text-slate-700" data-testid="export-school-static">
                {schoolName || '—'}
              </div>
            )}
            {loadingPreview && <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Hämtar…</p>}
            {preview && !loadingPreview && (
              <p className="text-xs text-slate-500 font-ibm" data-testid="export-active-count">
                {preview.active_count} aktiva sensorer{includeInactive ? '' : ''}
              </p>
            )}
          </div>

          {/* Missing MAC warning */}
          {preview && preview.missing_mac_count > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="missing-mac-warning">
              <p className="text-sm text-amber-800 font-ibm flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                {preview.missing_mac_count} av {preview.active_count} aktiva sensorer saknar Wi-Fi MAC-adress.
              </p>
              <button className="text-xs text-amber-700 underline mt-1 flex items-center gap-1" onClick={() => setShowMissing(v => !v)} data-testid="toggle-missing-mac">
                <ChevronDown className={cn("h-3 w-3 transition-transform", showMissing && "rotate-180")} />
                Visa sensorer som saknar MAC
              </button>
              {showMissing && (
                <ul className="mt-2 space-y-0.5 max-h-28 overflow-y-auto">
                  {preview.missing_mac_devices.map((d, i) => (
                    <li key={i} className="text-xs text-amber-900 font-ibm">• {d.name}{d.room ? ` — ${d.room}` : ''}</li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-amber-600 mt-1.5">Underlaget kan ändå exporteras — saknade MAC markeras som "Ej registrerad".</p>
            </div>
          )}

          {/* Format */}
          <div className="space-y-1.5">
            <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Exportformat</Label>
            <div className="flex gap-2">
              {[['pdf', FileText, 'PDF'], ['csv', Table2, 'CSV']].map(([val, Icon, lbl]) => (
                <button key={val} onClick={() => setFormat(val)} data-testid={`format-${val}`}
                  className={cn("flex-1 flex items-center justify-center gap-2 h-10 rounded-md border text-sm font-ibm transition-colors",
                    format === val ? "border-navy bg-navy text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300")}>
                  <Icon className="h-4 w-4" />{lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Columns */}
          <div className="space-y-1.5">
            <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Inkludera</Label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {COLUMNS.map(c => (
                <label key={c.key} className="flex items-center gap-2 text-sm font-ibm text-slate-700 cursor-pointer" data-testid={`col-${c.key}`}>
                  <Checkbox checked={cols.includes(c.key)} onCheckedChange={() => toggleCol(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          {/* Advanced */}
          <label className="flex items-center gap-2 text-sm font-ibm text-slate-600 cursor-pointer" data-testid="include-inactive">
            <Checkbox checked={includeInactive} onCheckedChange={(v) => setIncludeInactive(!!v)} />
            Inkludera inaktiva sensorer
          </label>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleCopyMac} disabled={!preview} className="font-ibm gap-2 sm:mr-auto" data-testid="copy-mac-btn">
            <Copy className="h-4 w-4" /> Kopiera MAC-lista
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="font-ibm">Avbryt</Button>
          <Button onClick={handleExport} disabled={exporting || !schoolId} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="do-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exportera
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
